/**
 * Floating bulk action toolbar for CRMObjectListPage.
 * Appears at the bottom center of the screen when one or more rows are selected.
 * Uses a portal to render at document root, avoiding overflow clipping.
 *
 * Supports standard actions (Delete, Export, Assign) plus
 * entity-type-specific actions via the `additionalActions` prop.
 *
 * Responsive behaviour:
 *   - Desktop (≥ sm): actions render as labeled inline buttons.
 *   - Mobile (< sm): actions collapse into a single labeled "Actions" menu so
 *     users get text labels (not ambiguous icon-only buttons) and the bar stays
 *     within the viewport. Built on the shared `Dropdown` primitive.
 */

import {
  ChevronDown,
  Loading02,
  Trash01,
  Upload01,
  User01,
  XClose,
} from '@untitledui/icons';
import type { FC, Key, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/lib/base/buttons/button';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { cx } from '@/utils/cx';

/** A single entity-type-specific action shown in the bulk bar. */
export interface BulkAction {
  /** Unique key for this action */
  key: string;
  /** Display label */
  label: string;
  /** Icon component (from @untitledui/icons) */
  icon?: FC<{ className?: string }>;
  /** Callback when the action is triggered */
  onAction: (selectedIds: string[]) => void;
  /** Whether this action is destructive (renders in error color) */
  destructive?: boolean;
  /** Whether this action is disabled */
  disabled?: boolean;
}

export interface BulkActionBarProps {
  /** IDs of the currently selected rows */
  selectedIds: string[];
  /** Callback to clear the selection */
  onClearSelection: () => void;
  /** Callback when Delete is triggered */
  onDelete?: (selectedIds: string[]) => void;
  /** Callback when Export is triggered */
  onExport?: (selectedIds: string[]) => void;
  /** Whether export is in progress */
  isExporting?: boolean;
  /** Callback when Assign is triggered */
  onAssign?: (selectedIds: string[]) => void;
  /** Additional entity-type-specific actions */
  additionalActions?: BulkAction[];
  /** Whether "all" is selected (for displaying total count) */
  isAllSelected?: boolean;
  /** Total record count (used when isAllSelected is true) */
  totalCount?: number;
  /** Optional className override */
  className?: string;
}

/** Error styling for the destructive item inside the mobile menu. */
const DESTRUCTIVE_ITEM_CLASS = '[&_svg]:text-error-600 [&_span]:text-error-600';

/**
 * Internal normalized representation of every action, used to render both the
 * desktop inline buttons and the mobile overflow menu from a single source.
 */
interface NormalizedAction {
  key: string;
  label: string;
  /**
   * Accessible label for the desktop icon-adjacent button. Defaults to `label`.
   * Standard actions use a more descriptive form (e.g. "Delete selected") that
   * the list-page tests and screen readers rely on; the mobile menu shows the
   * shorter visible `label` text instead.
   */
  ariaLabel?: string;
  icon?: FC<{ className?: string }>;
  onAction: (selectedIds: string[]) => void;
  destructive?: boolean;
  disabled?: boolean;
}

/** Renders a single desktop inline action button with a stable click handler. */
function ActionButton({
  action,
  onAction,
  isExporting,
}: {
  action: NormalizedAction;
  onAction: (key: Key) => void;
  isExporting: boolean;
}) {
  const handleClick = useCallback(
    () => onAction(action.key),
    [onAction, action.key]
  );
  return (
    <Button
      color={action.destructive ? 'tertiary-destructive' : 'tertiary'}
      size='sm'
      iconLeading={action.icon}
      onClick={handleClick}
      isDisabled={action.disabled}
      aria-label={action.ariaLabel ?? action.label}
      className={
        action.key === '__export' && isExporting ? '[&_svg]:animate-spin' : ''
      }
    >
      {action.label}
    </Button>
  );
}

/**
 * BulkActionBar renders a floating toolbar at the bottom center of the screen
 * when rows are selected. Uses a portal to avoid overflow clipping.
 * It is conditionally rendered — only mount it when selectedIds.length > 0.
 */
export function BulkActionBar({
  selectedIds,
  onClearSelection,
  onDelete,
  onExport,
  isExporting = false,
  onAssign,
  additionalActions = [],
  isAllSelected = false,
  totalCount,
  className,
}: BulkActionBarProps) {
  // Normalize every action into one ordered list. Order mirrors the desktop
  // layout: Export, Assign, entity-specific actions, then Delete (destructive)
  // last. The menu reuses this list so the two layouts never drift.
  const actions = useMemo<NormalizedAction[]>(() => {
    const list: NormalizedAction[] = [];
    if (onExport) {
      list.push({
        key: '__export',
        label: isExporting ? 'Exporting…' : 'Export',
        ariaLabel: isExporting ? 'Exporting…' : 'Export selected',
        icon: isExporting ? Loading02 : Upload01,
        onAction: onExport,
        disabled: isExporting,
      });
    }
    if (onAssign) {
      list.push({
        key: '__assign',
        label: 'Assign',
        ariaLabel: 'Assign selected',
        icon: User01,
        onAction: onAssign,
      });
    }
    for (const action of additionalActions) {
      list.push(action);
    }
    if (onDelete) {
      list.push({
        key: '__delete',
        label: 'Delete',
        ariaLabel: 'Delete selected',
        icon: Trash01,
        onAction: onDelete,
        destructive: true,
      });
    }
    return list;
  }, [onExport, isExporting, onAssign, additionalActions, onDelete]);

  const actionByKey = useMemo(
    () => Object.fromEntries(actions.map(a => [a.key, a])),
    [actions]
  );

  const handleMenuAction = useCallback(
    (key: Key) => {
      const action = actionByKey[String(key)];
      if (action && !action.disabled) {
        action.onAction(selectedIds);
      }
    },
    [actionByKey, selectedIds]
  );

  // When "all" is selected, show total count; otherwise show selected IDs count
  const count =
    isAllSelected && totalCount !== undefined ? totalCount : selectedIds.length;

  if (count === 0) {
    return null;
  }

  const clearButton = (
    <Button
      color='tertiary'
      size='sm'
      iconLeading={XClose}
      onClick={onClearSelection}
      aria-label='Clear selection'
    />
  );

  const countLabel = (
    <span className='text-xs sm:text-sm font-medium text-fg-secondary whitespace-nowrap'>
      {count.toLocaleString()} selected
    </span>
  );

  // Layout is toggled with CSS, not JS: both variants are in the DOM and the
  // active one is chosen by the `sm` breakpoint. This avoids a viewport-read
  // hook (and the render flash / SSR mismatch that comes with it) and keeps the
  // two layouts in sync from the single `actions` list.
  let actionsContent: ReactNode = null;
  if (actions.length > 0) {
    actionsContent = (
      <>
        {/* Desktop (≥ sm): labeled inline buttons. */}
        <div className='hidden items-center sm:flex'>
          <div className='mx-2 h-4 w-px shrink-0 bg-border-secondary' />
          <div className='flex items-center gap-1'>
            {actions.map(action => (
              <ActionButton
                key={action.key}
                action={action}
                onAction={handleMenuAction}
                isExporting={isExporting}
              />
            ))}
          </div>
        </div>

        {/* Mobile (< sm): collapse into a single labeled "Actions" menu so
            users get text labels instead of ambiguous icon-only buttons. */}
        <div className='flex items-center sm:hidden'>
          <div className='mx-1.5 h-4 w-px shrink-0 bg-border-secondary' />
          <Dropdown.Root>
            <Button
              color='secondary'
              size='sm'
              iconTrailing={ChevronDown}
              aria-label='Bulk actions menu'
            >
              Actions
            </Button>
            {/* Bar sits at the bottom of the screen, so open the menu upward. */}
            <Dropdown.Popover placement='top end'>
              <Dropdown.Menu selectionMode='none' onAction={handleMenuAction}>
                {actions.map(action => (
                  <Dropdown.Item
                    key={action.key}
                    id={action.key}
                    label={action.label}
                    icon={action.icon}
                    isDisabled={action.disabled}
                    className={
                      action.destructive ? DESTRUCTIVE_ITEM_CLASS : undefined
                    }
                  />
                ))}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown.Root>
        </div>
      </>
    );
  }

  const toolbar = (
    <div
      role='toolbar'
      aria-label='Bulk actions'
      className={cx(
        // Fixed positioning at bottom center
        'fixed bottom-6 left-1/2 -translate-x-1/2 z-50',
        // Styling — compact on mobile, full size on desktop
        'flex items-center gap-1.5 sm:gap-2 rounded-xl border border-border-secondary bg-bg-primary px-2.5 sm:px-4 py-1.5 sm:py-2.5 shadow-lg',
        // Animation
        'animate-in fade-in-0 slide-in-from-bottom-4 duration-200',
        className
      )}
    >
      {/* Clear selection (X button first, like Attio) */}
      {clearButton}

      {/* Selection count */}
      {countLabel}

      {actionsContent}
    </div>
  );

  // Use portal to render at document root, avoiding overflow clipping
  if (typeof document !== 'undefined') {
    return createPortal(toolbar, document.body);
  }

  return toolbar;
}
